import { useState, useMemo } from "react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { ChevronLeft, ChevronRight, Receipt, CreditCard } from "lucide-react";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

export const BillsCalendar = ({ bills, creditCards = [], onBillClick, formatCurrency }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  
  // Get first day of month and total days
  const firstDayOfMonth = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrevMonth = new Date(year, month, 0).getDate();
  
  // Combine bills and credit cards into unified items
  const allItems = useMemo(() => {
    const items = [];
    
    // Add regular bills
    bills.forEach(bill => {
      items.push({
        ...bill,
        type: 'bill',
        displayName: bill.merchant_display || bill.merchant,
        dueDate: bill.next_due_date,
        amount: bill.avg_amount
      });
    });
    
    // Add credit cards as calendar items
    creditCards.forEach(card => {
      items.push({
        id: `cc-${card.id}`,
        type: 'credit_card',
        displayName: card.name,
        dueDate: card.next_due_date,
        amount: card.balance,
        mask: card.mask,
        institution_name: card.institution_name,
        utilization: card.utilization,
        originalCard: card
      });
    });
    
    return items;
  }, [bills, creditCards]);
  
  // Group items by date
  const itemsByDate = useMemo(() => {
    const grouped = {};
    allItems.forEach(item => {
      const dueDate = item.dueDate;
      if (!grouped[dueDate]) {
        grouped[dueDate] = [];
      }
      grouped[dueDate].push(item);
    });
    return grouped;
  }, [allItems]);
  
  // Navigate months
  const goToPrevMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };
  
  const goToNextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };
  
  const goToToday = () => {
    setCurrentDate(new Date());
  };
  
  // Build calendar grid
  const calendarDays = useMemo(() => {
    const days = [];
    
    // Previous month days
    for (let i = firstDayOfMonth - 1; i >= 0; i--) {
      const day = daysInPrevMonth - i;
      const date = new Date(year, month - 1, day);
      days.push({
        day,
        date,
        dateStr: date.toISOString().split("T")[0],
        isCurrentMonth: false,
        isToday: false
      });
    }
    
    // Current month days
    const today = new Date();
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const isToday = 
        date.getDate() === today.getDate() &&
        date.getMonth() === today.getMonth() &&
        date.getFullYear() === today.getFullYear();
      days.push({
        day,
        date,
        dateStr: date.toISOString().split("T")[0],
        isCurrentMonth: true,
        isToday
      });
    }
    
    // Next month days (fill remaining cells)
    const remainingCells = 42 - days.length; // 6 rows x 7 days
    for (let day = 1; day <= remainingCells; day++) {
      const date = new Date(year, month + 1, day);
      days.push({
        day,
        date,
        dateStr: date.toISOString().split("T")[0],
        isCurrentMonth: false,
        isToday: false
      });
    }
    
    return days;
  }, [year, month, firstDayOfMonth, daysInMonth, daysInPrevMonth]);
  
  // Calculate total for month (bills + credit card balances)
  const monthTotal = useMemo(() => {
    return allItems.reduce((sum, item) => {
      const itemDate = new Date(item.dueDate);
      if (itemDate.getMonth() === month && itemDate.getFullYear() === year) {
        return sum + item.amount;
      }
      return sum;
    }, 0);
  }, [allItems, month, year]);

  const handleItemClick = (item) => {
    if (item.type === 'credit_card') {
      // For credit cards, we don't open the bill edit modal
      // Could navigate to a credit card detail or do nothing
      return;
    }
    // For regular bills, use the onBillClick handler
    onBillClick(item);
  };
  
  return (
    <Card className="border-border/50">
      <CardContent className="p-4 sm:p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={goToPrevMonth}
              data-testid="calendar-prev-month"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={goToNextMonth}
              data-testid="calendar-next-month"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <h2 className="font-heading font-bold text-lg ml-2">
              {MONTHS[month]} {year}
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <p className="text-xs text-muted-foreground">This month</p>
              <p className="font-semibold text-primary">{formatCurrency(monthTotal)}</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={goToToday}
              className="rounded-full"
            >
              Today
            </Button>
          </div>
        </div>
        
        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-px bg-border/50 rounded-xl overflow-hidden">
          {/* Day headers */}
          {DAYS.map(day => (
            <div
              key={day}
              className="bg-muted/50 p-2 text-center text-xs font-medium text-muted-foreground"
            >
              {day}
            </div>
          ))}
          
          {/* Calendar cells */}
          {calendarDays.map((dayInfo, index) => {
            const dayItems = itemsByDate[dayInfo.dateStr] || [];
            const hasUrgentItem = dayItems.some(item => {
              const daysUntil = Math.ceil((new Date(item.dueDate) - new Date()) / (1000 * 60 * 60 * 24));
              return daysUntil <= 3 && daysUntil >= 0;
            });
            
            return (
              <div
                key={index}
                className={`
                  bg-background min-h-[80px] sm:min-h-[100px] p-1 sm:p-2 
                  ${!dayInfo.isCurrentMonth ? "bg-muted/30" : ""}
                  ${dayInfo.isToday ? "ring-2 ring-primary ring-inset" : ""}
                `}
              >
                {/* Day number */}
                <div className={`
                  text-xs sm:text-sm font-medium mb-1
                  ${!dayInfo.isCurrentMonth ? "text-muted-foreground/50" : ""}
                  ${dayInfo.isToday ? "text-primary font-bold" : ""}
                `}>
                  {dayInfo.day}
                </div>
                
                {/* Items for this day */}
                <div className="space-y-1">
                  {dayItems.slice(0, 3).map((item, itemIndex) => {
                    const isCreditCard = item.type === 'credit_card';
                    const isUrgent = hasUrgentItem && itemIndex === 0;
                    
                    return (
                      <button
                        key={item.id}
                        onClick={() => handleItemClick(item)}
                        className={`
                          w-full text-left p-1 sm:p-1.5 rounded-md text-xs truncate
                          transition-colors ${isCreditCard ? 'cursor-default' : 'cursor-pointer'}
                          ${isCreditCard
                            ? "bg-violet-100 dark:bg-violet-900/30 text-violet-800 dark:text-violet-200 hover:bg-violet-200 dark:hover:bg-violet-900/50"
                            : isUrgent
                              ? "bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 hover:bg-amber-200 dark:hover:bg-amber-900/50"
                              : "bg-primary/10 text-primary hover:bg-primary/20"
                          }
                        `}
                        data-testid={`calendar-item-${item.id}`}
                        title={isCreditCard ? `${item.displayName} - ${formatCurrency(item.amount)} due` : undefined}
                      >
                        <span className="flex items-center gap-1">
                          {isCreditCard && <CreditCard className="h-2.5 w-2.5 flex-shrink-0" />}
                          <span className="hidden sm:inline truncate">{item.displayName}</span>
                          <span className="sm:hidden truncate">
                            {item.displayName.slice(0, 8)}
                            {item.displayName.length > 8 ? "..." : ""}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                  {dayItems.length > 3 && (
                    <div className="text-xs text-muted-foreground pl-1">
                      +{dayItems.length - 3} more
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Legend */}
        <div className="flex items-center gap-4 mt-4 text-xs text-muted-foreground flex-wrap">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded bg-primary/20" />
            <span>Bill due</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded bg-violet-200 dark:bg-violet-900/50" />
            <span>Credit card</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded bg-amber-200 dark:bg-amber-900/50" />
            <span>Due soon</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded ring-2 ring-primary" />
            <span>Today</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default BillsCalendar;
